import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const isTokenExpired = (tokenString) => {
  try {
    if (!tokenString) return true;
    let raw = tokenString;
    try {
      raw = JSON.parse(tokenString);
    } catch (_) {}
    if (typeof raw !== "string") return true;
    const parts = raw.split(".");
    if (parts.length !== 3) return true;
    const base64Url = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64Url));
    if (!payload.exp) return false;
    return Date.now() >= (payload.exp - 60) * 1000;
  } catch (_) {
    return true;
  }
};

const getSessionIdFromToken = (tokenString) => {
  try {
    if (!tokenString) return null;
    let raw = tokenString;
    try {
      raw = JSON.parse(tokenString);
    } catch (_) {}
    if (typeof raw !== "string") return null;
    const parts = raw.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(base64Url));
    return payload.sessionId || null;
  } catch (_) {
    return null;
  }
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referÃªncia dos listeners ativos
  const listenersRef = useRef(new Set());

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
          setIsAuth(true);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};

        if (error?.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data?.token) {
              localStorage.setItem("token", JSON.stringify(data.token));
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
            }
            return api(originalRequest);
          } catch (refreshErr) {
            const isRevoked = refreshErr?.response?.data?.error === "ERR_SESSION_REVOKED" || refreshErr?.response?.data?.message === "ERR_SESSION_REVOKED";
            if (isRevoked) {
              localStorage.removeItem("token");
              localStorage.removeItem("sessionId");
              api.defaults.headers.Authorization = undefined;
              setIsAuth(false);
              toastError("Tu sesion fue cerrada porque se alcanzo el limite maximo de 2 dispositivos activos.");
              window.location.href = "/login";
              return Promise.reject(refreshErr);
            }
            const currentToken = localStorage.getItem("token");
            if (isTokenExpired(currentToken)) {
              localStorage.removeItem("token");
              api.defaults.headers.Authorization = undefined;
              setIsAuth(false);
            }
            return Promise.reject(error);
          }
        }

        if (error?.response?.status === 401) {
          const isRevoked = error?.response?.data?.error === "ERR_SESSION_REVOKED" || error?.response?.data?.message === "ERR_SESSION_REVOKED";
          const currentToken = localStorage.getItem("token");
          const isRefreshRequest = originalRequest?.url?.includes("/auth/refresh_token");

          // Si la sesion fue expresamente revocada por limite de dispositivos, expulsamos siempre
          if (isRevoked) {
            localStorage.removeItem("token");
            localStorage.removeItem("sessionId");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
            toastError("Tu sesion fue cerrada porque se alcanzo el limite maximo de 2 dispositivos activos.");
            window.location.href = "/login";
            return Promise.reject(error);
          }

          // Si el endpoint que dio 401 fue /auth/refresh_token pero el token local sigue vigente (< 8h),
          // no expulsamos al usuario por una falla transitoria de red en movil.
          if (isRefreshRequest && !isTokenExpired(currentToken)) {
            return Promise.reject(error);
          }

          localStorage.removeItem("token");
          localStorage.removeItem("sessionId");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        let parsedToken = "";
        try {
          parsedToken = JSON.parse(token);
        } catch (_) {
          parsedToken = token;
        }

        // Si el token actual sigue vigente, lo activamos de inmediato
        if (parsedToken && !isTokenExpired(token)) {
          api.defaults.headers.Authorization = `Bearer ${parsedToken}`;
          setIsAuth(true);
        }

        try {
          const { data } = await api.post("/auth/refresh_token");
          if (data?.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            setIsAuth(true);
            setUser(data.user || data);
          }
        } catch (err) {
          if (isTokenExpired(token)) {
            toastError(err);
          }
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuraÃ§Ã£o do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      if (process.env.NODE_ENV !== "production") console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexÃ£o socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket estÃ¡ configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o mÃ©todo 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            if (process.env.NODE_ENV !== "production") console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance nÃ£o tem mÃ©todo 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        if (process.env.NODE_ENV !== "production") console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // DependÃªncias especÃ­ficas

  // Effect para buscar dados do usuÃ¡rio atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          if (process.env.NODE_ENV !== "production")
            console.log("Erro ao buscar usuario atual:", err);
        }
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const applySessionData = (data) => {
    if (!data?.token || !data?.user) return;

    localStorage.setItem("token", JSON.stringify(data.token));
    localStorage.setItem("profileImage", data.user.profileImage || "");
    const sid = data.sessionId || getSessionIdFromToken(data.token);
    if (sid) {
      localStorage.setItem("sessionId", sid);
    }

    const companyDueDate = data.user?.company?.dueDate
      ? moment(data.user.company.dueDate).format("DD/MM/yyyy")
      : "";
    if (companyDueDate) {
      localStorage.setItem("companyDueDate", companyDueDate);
    }

    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    setUser(data.user);
    setIsAuth(true);
  };

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // LÃ³gica de configuraÃ§Ãµes da empresa (mantÃ©m como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        if (setting) {
          const signEnable = setting.value === "enabled";
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso atÃ© 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        const sid = data.sessionId || getSessionIdFromToken(data.token);
        if (sid) {
          localStorage.setItem("sessionId", sid);
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informaÃ§Ãµes! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("sessionId");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleEnterSupportMode = async (companyId, reason = "") => {
    try {
      setLoading(true);
      const { data } = await api.post("/support/mode/start", {
        companyId,
        reason
      });
      applySessionData(data);
      toast.success(`Modo soporte activo en ${data.user?.company?.name || "empresa"}`);
      setTimeout(() => {
        window.location.href = "/";
      }, 500);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExitSupportMode = async () => {
    try {
      setLoading(true);
      const { data } = await api.post("/support/mode/stop");
      applySessionData(data);
      toast.success("Modo soporte finalizado");
      setTimeout(() => {
        window.location.href = "/support";
      }, 500);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (process.env.NODE_ENV !== "production") console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    handleEnterSupportMode,
    handleExitSupportMode,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
